/**
 * Shared helpers for analytics data (Analytics page and Reports summary export)
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Label for a period key returned by /merchant/analytics (YYYY-MM-DD, IST)
 * @param {string} period - Period start date
 * @param {string} mode - daily | weekly | monthly
 * @returns {string} - e.g. "Aug 5, 2026", "Week of Aug 3, 2026", "Aug 2026"
 */
export const formatPeriodLabel = (period, mode) => {
  const [year, month, day] = period.split('-').map(Number);
  if (mode === 'monthly') return `${MONTHS[month - 1]} ${year}`;
  const label = `${MONTHS[month - 1]} ${day}, ${year}`;
  return mode === 'weekly' ? `Week of ${label}` : label;
};

/**
 * Commission rate for a row; shows a range when the rate changed within the period
 * @param {object} payin - Row payin block (effective_rate, min_rate, max_rate)
 * @returns {string} - e.g. "7.00%" or "7.00% – 7.50%"
 */
export const formatRate = (payin) => {
  if (payin.effective_rate == null) return '—';
  if (payin.min_rate != null && payin.max_rate - payin.min_rate >= 0.01) {
    return `${payin.min_rate.toFixed(2)}% – ${payin.max_rate.toFixed(2)}%`;
  }
  return `${payin.effective_rate.toFixed(2)}%`;
};

/**
 * Human-readable fee configuration
 * @param {object|null} fee - { calculation_type, percentage_value, flat_amount, slabs }
 * @returns {string}
 */
export const describeFee = (fee) => {
  if (!fee) return 'Not configured';
  if (fee.calculation_type === 'percentage') return `${Number(fee.percentage_value).toFixed(2)}%`;
  if (fee.calculation_type === 'flat') return `₹${Number(fee.flat_amount).toFixed(2)} per transaction`;
  if (fee.calculation_type === 'slab' && fee.slabs?.length) {
    return fee.slabs
      .map(s => `₹${s.min}–₹${s.max}: ${s.type === 'percentage' ? `${s.value}%` : `₹${s.value}`}`)
      .join(', ');
  }
  return fee.calculation_type;
};

const csvCell = (value) => {
  const text = value == null ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Build a CSV summary from /merchant/analytics data
 * @param {object} data - Analytics response data
 * @param {boolean} includeEmpty - Include periods with no transactions
 * @returns {string}
 */
export const buildAnalyticsCsv = (data, includeEmpty = false) => {
  const header = [
    'Period', 'Payin Amount', 'Successful Txns', 'Total Txns', 'Failed', 'Expired', 'Pending',
    'Success Rate %', 'Commission Rate', 'Commission', 'Net Payin',
    'Payout Amount', 'Successful Payouts', 'Total Payouts', 'Payout Fee'
  ];
  const line = (label, payin, payout, rate) => [
    label, payin.amount.toFixed(2), payin.success, payin.total, payin.failed, payin.expired, payin.pending,
    payin.success_rate.toFixed(2), rate, payin.fee.toFixed(2), payin.net_amount.toFixed(2),
    payout.amount.toFixed(2), payout.success, payout.total, payout.fee.toFixed(2)
  ].map(csvCell).join(',');

  const rows = data.rows
    .filter(r => includeEmpty || r.payin.total > 0 || r.payout.total > 0)
    .map(r => line(formatPeriodLabel(r.period, data.mode), r.payin, r.payout, formatRate(r.payin)));

  const totalRate = data.totals.payin.effective_rate != null
    ? `${data.totals.payin.effective_rate.toFixed(2)}%`
    : '—';
  rows.push(line('TOTAL', data.totals.payin, data.totals.payout, totalRate));

  return [header.map(csvCell).join(','), ...rows].join('\n') + '\n';
};

/**
 * Trigger a browser download for CSV text
 * @param {string} content - CSV text
 * @param {string} filename
 */
export const downloadCsv = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
